/**
 * 利用規約（Terms of Service）の単一ソース
 *
 * 重要:
 *   - 文言を変えるとユーザー体験は変わるが TOS_VERSION は手動で bump すること
 *   - 軽微な誤字・表記揺れの修正なら bump 不要
 *   - 実質的な内容変更（権利義務に影響）なら bump → 全ユーザー再同意
 *   - サーバー側 src/server/constants/tosVersion.ts と必ず同じ文字列にすること
 */

export const TOS_VERSION = '2026-04-22-v6'
/** ユーザー向け表示用ラベル（DB に格納する内部 ID とは別） */
export const TOS_VERSION_LABEL = '第6版'
export const TOS_LAST_UPDATED = '2026年4月22日'

export const OPERATOR_INFO = {
  companyName: '株式会社ラクダ',
  service: 'Genemo（Genemo Short / Genemo Page / Genemo Blog 等の Genemo シリーズ全般）',
} as const

export const SERVICE_DESCRIPTION =
  '本利用規約（以下「本規約」といいます。）は、本サービスの運営主体である株式会社ラクダ（以下「当社」といいます。）が提供する AI クリエイティブサービス「Genemo」（Genemo Short・Genemo Page・Genemo Blog 等、当社が「Genemo」ブランドのもとで提供する全てのサービスを含みます。以下「本サービス」といいます。）の利用条件を定めたものです。本サービスの利用には本規約への同意が必要です。'

export const AI_NOTICE =
  '本サービスで生成されるコンテンツはAIによる自動生成物であり、その内容・品質・正確性・合法性について当社は一切の保証を行いません。生成物の利用および利用結果に関するすべての責任はユーザー自身が負うものとします。'

export interface TosArticle {
  title: string
  content: string
}

export const TOS_ARTICLES: TosArticle[] = [
  {
    title: '第1条 定義',
    content: `本規約において使用する用語の意義は、次の各号に定めるとおりとします。

1.「本契約」とは、本サービスの利用に関して当社と「ユーザー」との間で締結される契約をいいます。

2.「ユーザー」とは、当社との間で本契約を締結して本サービスを利用する法人または個人をいいます。

3.「申込者」とは、本契約を締結して本サービスを利用することを希望する法人または個人をいいます。

4.「登録事項」とは、申込者が本契約の申込みの際に登録する当社所定の情報をいいます。

5.「ID・パスワード」とは、ユーザーが本サービスを利用するために必要となるIDおよびパスワードをいいます。

6.「無料トライアル」とは、有償である個別サービスを一定期間無料で試用することをいいます。

7.「利用料金」とは、本サービスの利用の対価として当社が定める料金をいいます。

8.「クレジット」とは、本サービスにおいてAI機能（動画生成・画像生成・台本生成等）を利用するために消費するポイント単位をいいます。

9.「生成コンテンツ」とは、本サービスを通じてAIが生成する動画・画像・音声・文章・台本その他一切のデータをいいます。

10.「当社ウェブサイト」とは、当社が「Genemo」ブランドのもとで運営するウェブサイト（ドメインまたは内容が変更された場合はその変更後のウェブサイトを含みます。）をいいます。

11.「Genemo シリーズ」とは、当社が「Genemo」ブランドのもとで提供するサービス群（Genemo Short、Genemo Page、Genemo Blog ほか今後追加されるものを含みます。）の総称をいいます。本規約は Genemo シリーズの全てのサービスに共通して適用されます。`,
  },
  {
    title: '第2条 適用',
    content: `1. 本規約は、本契約において当社とユーザーとの間の一切の関係に適用されるものとします。ユーザーは、本規約の全ての内容に同意した上で、本契約を当社と締結するものとします。

2. 当社が本サービスまたは当社ウェブサイト上等で掲載する本サービス利用に関するルールは、本規約の一部を構成するものとします。

3. 当社は、別途、個別規約を定める場合があります。個別規約は、本規約の一部を構成するものとしますが、本規約と個別規約の内容が異なる場合は、個別規約が優先して適用されます。`,
  },
  {
    title: '第3条 本規約の変更',
    content: `1. 当社は、以下の各号に掲げる場合には、本規約の内容を変更することができるものとします。

a. 法令等の改正に伴う変更が必要な場合

b. 本サービスの仕様変更に伴う変更の必要がある場合

c. 表現の変更または誤字脱字の修正等、本規約の内容に実質的に影響しない場合

d. その他本サービスを提供するうえで変更が合理的に必要な場合

2. 前項各号に定める以外の場合に本規約を変更する場合、当社は、効力発生日の2週間以上前にその旨を当社ウェブサイトに掲載することとし、これを周知します。

3. ユーザーが変更の効力発生日までに本サービスを解約しない場合、規約変更に同意したものとみなします。`,
  },
  {
    title: '第4条 契約の成立',
    content: `1. 申込者は、本規約の内容を承諾した上で、当社の指定する申込フォームに必要事項を記載し、当社に提出することにより、本サービスの利用申込を行うものとします。

2. 申込者は、本サービスの利用申込を、本契約を締結する正当な権限を有する者に行わせるものとし、本契約を締結する正当な権限を有しない者による申込を行わないものとします。

3. 当社は、以下のいずれかに該当する場合は、申込を拒否するもしくは取り消すことができます。なお、当社は、当該判断に関する理由を開示する義務は負いません。

a. 申込フォームの記載内容に虚偽の事実が存在する場合

b. 本サービスの料金等の支払いを怠るおそれがあると当社が判断したとき

c. 当社と競合するサービスを現に提供している場合又は将来提供するおそれがあると当社が判断したとき

d. 本規約のいずれかに違反している、またはそのおそれがあると当社が判断したとき

e. その他当社が本サービスの利用が妥当でないとした場合

4. 当社は、当社所定の期間、無料トライアルを利用プランやその他条件により、一度だけ利用させることがあります。この場合にユーザーは、無料トライアルの期間中に有償利用の可否を判断するものとし、その利用を希望しない場合は、無料トライアルの期間中に本サービスをキャンセルするものとします。なお、無料トライアルの利用に関しても、本規約が適用されます。

5. 特に定めのある場合を除き、ユーザーが無料トライアルの期間内に本サービスをキャンセルしない限り、無料トライアルの期間満了をもって、無料トライアルと同プランの有償プランに自動的に移行することにつき、ユーザーは同意したものとします。`,
  },
  {
    title: '第5条 利用期間等',
    content: `1. 本サービスの利用期間は、本契約にて定められている期間とします。

2. 利用期間終了日の前日までにユーザーまたは当社から相手方に対し更新しない旨の当社所定の方法による意思表示がない限り、当該時点と同一の条件（サービス内容、利用期間、利用料金等を含みます。）で契約延長するものとし、以後も同様とします。

3. 利用終了日が当初予定されていた利用期間の途中であっても、予定されていた利用期間に対する利用料金を支払うものとします。`,
  },
  {
    title: '第6条 設備の負担',
    content: `本サービスの提供を受けるために必要なコンピューター、ソフトウェアその他の機器、通信回線その他の通信環境等の準備及び維持は、ユーザーの費用と責任において行うものとします。`,
  },
  {
    title: '第7条 変更の届出',
    content: `ユーザーは、登録事項に変更が生じた場合、すみやかに当社に当該変更につき通知するものとします。これを怠ったことによってユーザーが損害を被ったとしても、当社は一切責任を負いません。`,
  },
  {
    title: '第8条 禁止事項',
    content: `ユーザーは本サービスの利用に関して、以下の行為を行わないものとします。

1. 本サービスの全部または一部を複製、変更、翻案等する行為

2. 逆アセンブル、リバース・エンジニアリング、デコンパイル等、本サービスのソースコードまたは本サービスの論理階層の解析、取得等を目論み、または実行する行為

3. 本サービスの全部または一部を、有償無償を問わず、当社の事前の承諾なしに、第三者（ユーザーのグループ会社またはフランチャイズ加盟店等を含みます。）に貸与または使用させる行為

4. 虚偽、不完全、不正確な情報を本サービスに登録または当社に届け出る行為

5. 当社またはその他の第三者の著作権、商標権等の知的財産権その他一切の権利を侵害し、または侵害するおそれのある行為

6. 第三者の肖像・著作物・商標等を無断で専属モデルとして登録する行為、またはそれを用いて生成コンテンツを作成する行為

7. 本サービスの生成コンテンツを利用して虚偽の事実または誤解を招く表示・広告を行う行為

8. 本サービスの利用目的に反する行為、または本規約に違反する行為

9. 本サービスの運営を妨げる行為、犯罪行為その他の法令違反に結びつく行為、または公序良俗に反する行為

10. 当社またはその他の第三者を誹謗中傷し、またはその名誉もしくは信用を毀損し、または毀損するおそれのある行為

11. 当社または第三者の機密情報もしくは個人情報を、その他の第三者に不正に公表、開示、提供および漏洩する行為

12. 他のユーザーの本サービスの利用を妨害する行為

13. **同一の自然人または同一の法人が、複数のアカウントを作成または保有する行為**（本人または当社の事前の書面による承諾がある場合、グループ会社による別アカウント保有等を除きます。以下「複数アカウント保有」といいます。）。なお、無料トライアルや特典の重複取得を目的とした別名義・別メールアドレス・別端末・別 IP からの登録も複数アカウント保有とみなします

14. **SNS、ブログ、レビューサイト、動画配信サービスその他いかなる媒体においても、虚偽の事実または誤解を招く表現により当社、本サービス、当社関係者、または他のユーザーの社会的信用、名誉、業務もしくはブランド価値を毀損し、または毀損するおそれのある投稿・拡散・示唆を行う行為**（事実に基づく適正な範囲のレビューや感想を表明する行為を除きます。）

15. そのほかユーザーへの本サービスの提供を継続することが不適切であると当社が判断するにいたる行為

【複数アカウント保有が発覚した場合の措置】
当社は、上記第13号に違反する複数アカウント保有が発覚した場合、事前の催告なく、当該ユーザーに紐付く全てのアカウントを直ちに停止または削除し、将来にわたって当該ユーザーによる本サービスの新規登録を拒否することができます。また、複数アカウント保有により当社に経済的損害（無料特典の不正取得、サポートコストの増加等を含みます。）が生じた場合、当社は当該ユーザーに対し、損害賠償を請求することができます。`,
  },
  {
    title: '第9条 カスタマーハラスメントの禁止',
    content: `1. 当社は、本サービスを相互の信頼関係に基づいて提供しています。ユーザーは、当社および当社の役員、従業員、委託先等（以下「当社関係者」といいます。）に対し、以下の各号に該当する行為（以下「カスタマーハラスメント」といいます。）を行ってはなりません。

a. 暴言、脅迫、威圧的な言動

b. 侮辱的、差別的、人格を否定するような言動

c. 社会通念上相当な範囲を超えた要求や、同一内容の過度な繰り返し

d. 長時間にわたる拘束、執拗な連絡、または業務時間外の連絡の強要

e. 当社関係者個人を攻撃する言動、または個人情報の詮索

f. SNS、レビューサイト等への誹謗中傷の投稿、またはその示唆による威圧

g. 当社が定める運用ルールへの繰り返しの違反、または改善要請への非協力

h. 当社からの連絡・確認事項に対する、合理的な期間内での応答の繰り返しの欠如

i. その他、当社関係者の尊厳を傷つけ、または業務遂行を妨げると当社が判断する言動

2. 当社は、ユーザーがカスタマーハラスメントに該当する行為を行ったと判断した場合、または信頼関係を維持することが困難であると判断した場合、事前の催告なく、直ちに本サービスの提供を停止し、または本契約を解除することができます。

3. 前項の措置を行う場合、当社は、当該判断の理由を開示する義務を負いません。また、当社は、本条に基づく措置によりユーザーに生じた損害について、一切の責任を負いません。

4. 本条に基づき本契約が解除された場合、ユーザーは、既に支払った利用料金の返還を請求することはできません。

5. 当社は、本サービスの提供にあたり、当社関係者の安全および健全な業務環境の確保を重視しており、本条に基づく対応はその方針に基づくものです。当社は、すべてのユーザーと対等かつ誠実なパートナーシップのもとでサービスを提供することを基本方針としています。`,
  },
  {
    title: '第10条 利用料金およびクレジット',
    content: `1. ユーザーは、当社に対し、本契約に定める利用料金を、本契約に定める支払方法に従って、本契約に定める支払期限までに支払うものとします。

2. その理由を問わず、本契約の期間中にユーザーが本サービスの提供を受けられなくなった場合又は受ける必要がなくなった場合でも、当社は利用料金の返還を行うことができません。

3. 本サービスの利用期間において、第15条に定める本サービスの停止により本サービスを利用することができない状態が生じたときであっても、別途の定めがない限り、ユーザーは、利用期間中の利用料金等の支払を要します。ただし、当社は、ユーザーに対し、当社の責に帰すべき事由により本サービスが一切利用できない状態が連続して24時間以上継続した場合で、かつ当該状態の是正確認後1ヶ月以内にユーザーが当社所定の方法で請求を行った場合、利用できなかった日数につき利用料金を日割りで返金するものとします。

4. ユーザーが利用料金の支払を遅延した場合、年14.5%の割合による遅延損害金を当社に支払うものとします。

5. 当社は、本サービスの内容の変更、事業上の理由、法令の制定改廃、経済情勢の変動等によって、利用料金の変更の必要が生じたときは、本サービスの利用料金を変更することができるものとします。**利用料金を値上げする場合、当社は、効力発生日の1ヶ月以上前までに、ユーザーが当社に登録したメールアドレス宛に変更内容を通知するものとします。ユーザーが当該通知後、効力発生日までに本サービスを解約しない場合、ユーザーは値上げ後の料金に同意したものとみなします。**

5-2. ユーザーが契約期間の途中で上位のプランへ変更（アップグレード）した場合、当社は、変更後のプランの月間付与クレジット数から変更前のプランの月間付与クレジット数を差し引いた数（差引がゼロ以下となる場合は付与しません。）のクレジットを、変更時に付与します。変更時に新たに月間付与クレジットの全数を付与するものではありません。

6. クレジットには有効期限が設けられる場合があります。有効期限はプランまたはクレジット付与の条件により異なり、当社ウェブサイトまたはアプリ内に表示するものとします。

7. 有効期限を経過したクレジット、および本契約の終了時に残存するクレジットは、理由の如何を問わず失効するものとし、当社は返金・換金・繰越等を行いません。

8. AI機能の実行によりクレジットが消費された場合、生成コンテンツの品質・完成度・内容にかかわらず、当該クレジットの返還は行いません。生成処理が途中で失敗または中断した場合も同様とします。

【クレジット消費量の為替変動について】

8-2. 本サービスが利用する AI プロバイダー（OpenAI・Google Gemini・各種動画生成 API 等）の課金は米ドル建て等の外貨建てが基本となるため、**1 回の AI 処理（動画生成・画像生成・台本生成等）で消費されるクレジットの数量は、その時点の外国為替相場（USD/JPY 等）の変動に応じて日々変動します。**当社は、為替レートの変動によりクレジット消費量が増減することについて、事前の個別通知を行わず、また当該変動に起因する損害について一切の責任を負いません。

8-3. 当社は、円安進行や AI プロバイダーの料金改定等により大幅な為替変動・コスト変動が生じた場合、合理的な範囲でクレジット消費レートの計算式または基準レートを調整することができます。当該調整は当社ウェブサイトまたはアプリ内に表示することにより周知するものとし、ユーザーは当該表示をもって当該調整に同意したものとみなします。

8-4. 動画生成・画像生成・音声生成・テキスト生成等の AI 機能を利用する際に消費されるクレジットの単価（以下「コスト単価」といいます。）は、当社が独自に定める基準に従って算出されます。コスト単価は、使用する AI モデルの種類・処理内容・品質設定等に応じて異なり、かつ外国為替相場の変動および各 AI プロバイダーが定める API 利用料金の改定を鑑みて随時変動します。ユーザーは、事前に表示される消費クレジットの見積もりはあくまで概算であり、実際の消費量がこれと異なる場合があることをあらかじめ承諾するものとします。

【決済代行サービス（Stripe）について】

9. 当社は、本サービスの利用料金の決済処理を、決済代行業者である Stripe Inc.（および Stripe, Inc. の関連会社、以下総称して「Stripe」といいます。）を通じて行います。ユーザーは、本サービスの有償プランの申込み、追加クレジットの購入、その他本サービスに関する決済を行う場合、Stripe の利用規約およびプライバシーポリシーにも同意したものとみなします。

10. クレジットカード番号、セキュリティコードその他の支払い情報は、Stripe が PCI DSS（Payment Card Industry Data Security Standard）に準拠した安全な環境で直接保管・処理します。当社は、これらの支払い情報を直接保管しません。

11. 当社は、Stripe から受領する決済結果（成功・失敗・チャージバック等）に基づいて利用料金の収納を確定し、サブスクリプションの開始・継続・終了、クレジットの付与・消費等の処理を行います。

12. サブスクリプションプランは、ユーザーが解約手続きを行わない限り、契約期間満了をもって同一条件で自動的に更新されます。自動更新に伴う利用料金は、Stripe 経由で登録済みのお支払い方法から自動的に課金されます。

13. ユーザーがサブスクリプションプランの解約手続きを行った場合、解約は当該請求期間の末日をもって効力を生じるものとし、当該請求期間内の利用料金は返金されません。請求期間内は引き続き本サービスを利用できます。

14. クレジットカード情報の変更、明細の確認、サブスクリプションの解約等は、本サービス内の所定の画面または Stripe が提供するカスタマーポータルから行うことができます。

15. ユーザーが正当な理由なくクレジットカード会社等を通じてチャージバック（売上取消請求）を行った場合、当社は当該ユーザーのアカウントを直ちに停止または解除し、また、被った損害（決済手数料・調査コストを含みます。）の賠償を請求することができます。

16. 為替変動・税制改正・Stripe の手数料変更等により、当社が表示する利用料金（税込・各種通貨）は変動する場合があります。

【領収書・適格請求書（インボイス）について】

17. 当社は、個別の領収書・適格請求書（インボイス）の発行は行いません。お支払いの領収書・請求書は、Stripe が発行する電子領収書（PDF）をご利用ください。本サービス内の「設定 → 課金管理」または Stripe カスタマーポータルにログイン後、ご自身でダウンロードいただけます。Stripe 発行の領収書には、当社の適格請求書発行事業者登録番号（T9140001136960）が記載されています。

【アカウントの削除について】

18. 本サービスでは、ユーザーによるアカウントの削除機能は提供しておりません。本サービスのご利用を終了される場合は、サブスクリプションの解約手続きをもってサービス利用の終了とみなします。解約後もアカウント情報および生成データは当社サーバー上に一定期間保持される場合があります。`,
  },
  {
    title: '第11条 委託',
    content: `当社は、本サービスに関して自己が行うべき業務の全部または一部を第三者に委託することができるものとします。この場合、当社は、当該委託先を適切に監督するものとし、当該委託先の行為につき連帯してその責任を負うものとします。`,
  },
  {
    title: '第12条 ID・パスワードの発行',
    content: `1. 当社は、本サービスの提供に際し、ユーザーごとにIDとパスワードを発行の上、これを付与します。

2. ユーザーは、当社の事前の承諾がないかぎり、ID・パスワードを第三者に利用させることはできません。また、ID・パスワードを複数の個人または法人で共有したり、譲渡することはできません。

3. ユーザーは、ID・パスワードの使用および管理について一切の責任を負うものとし、盗難、紛失、不正使用、および他人による無断使用等の場合を含め、ユーザーに責任があると否とを問わず、当社は、ID・パスワードの使用および管理から生じた一切の損害について一切責任も負いません。ただし、当社の責に帰すべき事由がある場合はこの限りではありません。`,
  },
  {
    title: '第13条 バックアップおよびデータ保管',
    content: `1. ユーザーは、ユーザーが本サービスを利用して作成または保存した情報・データ（プロジェクトデータ、アップロード画像、生成された動画・画像・音声・台本・文章その他一切のコンテンツを含みます。以下「データ等」といいます。）について、自らの責任でバックアップを行うものとし、当社は、ユーザーによるデータ等の保管、保存、バックアップ等に関して、一切責任を負いません。

2. **ユーザーは、本サービス上で生成・保存したデータ等のうち重要なものについて、定期的に自己の端末またはストレージへダウンロードしてバックアップを取得するものとします。**当社は、ユーザーがバックアップを怠ったことに起因する損害について、一切の責任を負いません。

3. 当社は、本サービスに関して保守、運用上または技術上必要であると判断した場合、データ等について、監視、分析、調査等必要な行為を行うことがあります。

4. 当社は、プロジェクトデータ、生成コンテンツ（動画・画像・音声等）その他ユーザーのデータ等を永続的に保管する義務を負いません。

5. **当社は、以下のいずれかに該当する場合、事前の個別通知なく、ユーザーのデータ等の全部または一部を削除することができます。削除されたデータ等は復元できません。**

a. 長期間（当社が別途定める期間）ユーザーによるアクセスまたは利用がない場合

b. 一定期間経過後の古い生成コンテンツ・古い中間ファイル・未使用のアップロード素材等、ストレージ容量の最適化のため当社が削除を要すると判断した場合

c. 本契約が終了した場合、または当社が本サービスの全部もしくは一部を終了した場合

d. 法令違反、規約違反、または第三者の権利侵害のおそれがあると当社が判断した場合

e. その他本サービスの安定運営または当社の事業上必要であると当社が判断した場合

6. 当社は、ユーザーのデータの滅失、毀損、漏洩、削除その他の損害について、当社の故意または重過失による場合を除き、一切の責任を負いません。**重要なデータは必ずユーザー側で別途バックアップを保持してください。**`,
  },
  {
    title: '第14条 当社による契約解除',
    content: `1. ユーザーが次の各号のいずれかに該当すると当社が判断した場合には、当社は事前に通知・催告することなく、本サービスの停止又は解除することができます。なお、当社は、上記判断に関する理由を開示する義務は負いません。

a. 本規約の各条項のいずれかに違反した場合

b. 利用料金等の支払債務の一部または全部の履行を遅滞し、または正当な理由なく支払を拒絶した場合

c. ユーザーがID等の適切な管理を怠った場合および第三者に使用させるなど不適切な利用をしたり試みたりした場合

d. 過去に当社から本規約違反などで解除されたことが判明した場合

e. 本サービスを利用して違法な行為を行った場合

f. 手形および小切手の不渡り、事実上の支払不能もしくはこれに準ずる状態に陥り、または破産、民事再生手続開始、会社更生手続開始、特別清算手続き開始もしくはこれらに類する手続きの開始の申立を受け、または自ら申し立てた場合

g. 差押え、仮差押えもしくは競売の申立があったとき、または公租公課の滞納処分を受けた場合

h. 監督官庁から営業許可の取消、停止等の処分を受けた場合

i. 解散、減資、事業（営業）の全部または重要な一部の譲渡等の着手や決議をした場合

j. ユーザーが14日以上にわたって連絡不能となった場合

k. 本規約の変更に同意しない場合

l. 第8条第13号に定める複数アカウント保有が発覚した場合

m. 第8条第14号に定める SNS 等での悪評拡散行為があった場合

n. 第10条第15号に定めるチャージバックを正当な理由なく行った場合

o. その他当社が、ユーザーと当社との間の信頼関係の維持が困難と判断した場合

2. ユーザーが第1項に基づく本サービスの利用の一時停止の措置を受けている場合であっても、本契約が継続している限り、利用料金を支払う義務を負うものとします。

3. ユーザーは第1項に該当する行為によって、当社に損害等を生じさせた場合は、その損害等の賠償をしなければなりません。

4. 当社は第1項に該当する行為によって、ユーザーに生じた損害について、一切の責任を負いません。

5. 当社は第1項の解除をした場合も、損害賠償請求権を放棄するものではありません。`,
  },
  {
    title: '第15条 本サービスの停止',
    content: `1. 当社は、以下のいずれかの事由があると判断した場合、ユーザーへ事前に通知の上、本サービスの内容を変更し、または、本サービスの正常な提供を行うのに必要な期間、本サービスの提供を一時的に停止することができます。ただし、緊急にサービスの提供を停止する必要性が高いと判断した場合等、事前通知が困難な場合には、ユーザーへの事後の通知をもってこれに替えることができるものとします。

a. 本サービスの定期保守、点検、もしくは更新を行う場合

b. 火災、停電、天災地変などの不可抗力により、本サービスの提供が困難もしくは不可能な場合

c. 突発的な本サービスの故障等が発生した場合

d. 本サービスが利用している外部サービスの中断、中止その他障害の場合

e. その他不測の事態の発生により、本サービスの提供が困難もしくは不可能な場合

2. 当社は、前項に基づく本サービスの全部または一部の停止による損害等に関し、ユーザーまたは第三者に損害等が発生した場合でも、当社に故意または重過失がある場合を除き、一切の責任を負わない。`,
  },
  {
    title: '第16条 本サービスの変更、中止または終了',
    content: `1. 当社は、当社の判断により、本サービスの全部または一部の内容を変更、追加、中止または終了することができます。

2. 当社は、前項により、本サービスの全部または一部の内容を変更、追加、中止または終了する場合は、あらかじめユーザーに通知します。ただし、緊急やむを得ない場合は、この限りではありません。

3. 第1項に基づき、第5条の利用期間中であっても本サービスの全部または一部の提供を終了するものとし、終了日をもって利用契約の全部または一部を解約することができるものとします。

4. 第1項に基づき、当社は、当初予定していた契約期間の途中で本サービスの全部の内容を終了したユーザーに対し、すでに受領した利用料金から本サービスを利用した日数分に相当する額の利用料金を差し引いた金額を返還する。

5. 当社は、定期的に機能追加または改修などを行い、本サービスをバージョンアップする場合があります。その際、従来のバージョンについては予告なく利用できなくなることがあります。`,
  },
  {
    title: '第17条 不保証および免責',
    content: `1. ユーザーは、自己の責任により本サービスを利用するものとし、本サービスの利用およびその準備に伴いユーザーに生じた損害について当社は責任を負いません。ただし、ユーザーがこうむった損害が当社の故意または重過失に起因する場合、当社は第23条（損害賠償）の定めに従いユーザーの損害を賠償するものとします。

2. 当社は、ユーザーに対し、本サービスが正確かつ完全であること、停止などなく永続的に稼働すること、エラー・バグ・不具合又はセキュリティ上の欠陥が存在しないこと、その利用に起因して利用環境に不具合や障害が生じないこと、ユーザーが期待する効果等を有していること、ユーザーの利用目的に適合していること、ユーザーに適用のある法令又は業界団体の内部規則に適合することについて、何ら保証するものではありません。

3. 当社は、本サービスを利用することで出力・表示されるデータが正確であるよう、商業上合理的な範囲内で努力しますが、正確性についての保証は行いません。ユーザーは、本サービスを利用することで出力されるデータの内容を確認の上、自己の責任に基づいてこれを利用するものとします。

4. 当社は、本サービスが利用する外部サービスの仕様変更や機能追加などによる本サービスの中断、中止その他障害によって本サービスの一部または全部をご利用いただけない場合があることおよびそれにより生じた損害については当社が責任を負わないことをあらかじめ了承するものとします。

5. ユーザーは、ユーザーのコンピューター端末におけるシステム環境によって本サービスの一部または全部をご利用いただけない場合があることおよびそれにより生じた損害については当社が責任を負わないことをあらかじめ了承するものとします。

6. ユーザーは、電気通信事業者、クラウドサービス事業者、ハードウェア事業者、その他の第三者が提供するサービス・製品またはユーザーのコンピューター端末、通信環境等のユーザーのご利用環境に起因して生じた障害、逸失利益、データの喪失に伴う損害、および本サービスを利用したことにより生じた不利益等、当社の責に帰すべき事由によらないユーザーがこうむった損害について、当社が責任を負わないことをあらかじめ了承するものとします。

7. 当社は、本サービスが日本国外においても継続的に利用できること、または外国法令や規則等に適合することを保証するものではありません。本サービスは日本国内での利用を前提として提供されており、海外からのアクセスによる動作は保証しません。なお、ユーザーが自己の判断と責任においてVPN等を使用して本サービスを利用することは妨げませんが、その場合であっても動作不具合・接続障害・機能制限等に起因する損害について当社は一切の責任を負わず、また当該事由による返金・クレジット返還は行いません。

8. 当社は、本サービスにより生成される動画・画像・音声・文章その他一切の生成コンテンツについて、その品質・完成度・商業的利用可能性・特定目的への適合性を一切保証しません。生成コンテンツの内容・品質・正確性・合法性については当社が何ら保証するものではなく、これらに関するあらゆる損害・不利益についてユーザー自身が全責任を負うものとします。

9. 当社は、生成コンテンツが第三者の著作権、肖像権、商標権、プライバシー権その他一切の権利を侵害しないことを保証しません。ユーザーが生成コンテンツを利用したことにより第三者との間に生じた紛争・損害については、ユーザーが自己の責任と費用において解決するものとし、当社は一切の責任を負いません。

10. 本サービスが利用する外部サービス（OpenAI・Google Gemini 等の AI プロバイダー、Seedance 等の動画生成プロバイダー、Stripe 等の決済プロバイダー、Wasabi 等のストレージプロバイダーを含みます。）の障害・品質劣化・仕様変更・提供終了・料金変更に起因して生じた損害について、当社は一切の責任を負いません。

11. ユーザーが専属モデル機能・素材アップロード機能等に登録した画像・動画・音声・文章その他のコンテンツに関する著作権・肖像権・プライバシー権その他一切の権利に関する問題については、ユーザーの責任において処理するものとし、当社は一切の責任を負いません。

12. Genemo シリーズの各サービス（Genemo Short、Genemo Page、Genemo Blog 等）は、それぞれ独立した機能・仕様・料金体系を有する場合があり、特定のサービスの仕様や利用可能機能が他のサービスに必ずしも適用されるものではありません。`,
  },
  {
    title: '第18条 情報の利用等',
    content: `1. ユーザーは、当社が、本サービスに関して保守、運用上または技術上必要であると判断した場合、ユーザーとの契約上で特約において別途定める場合を除き、ユーザーのデータについて、監視、分析、調査等必要な行為を行うこと、並びにユーザーの本サービスの利用状況に関する統計データ、分析データまたは集合的データ（個人情報自体は含みません。）を、本サービスの利用状況の分析、本サービスの改善および当社の事業活動等に利用すること、もしくはどのユーザーに関する情報であるかを特定できない形式で第三者に開示することについて、あらかじめ同意するものとします。

2. 当社は、前項にかかわらず、本契約が終了した後であっても、以下の各号に定める目的のために、ユーザーの情報を必要な範囲で保持・利用します。

a. 過去に無料トライアルを利用したユーザーであるかを判別するなど、本サービスの適正な提供および不正利用の防止のため

b. 法令上の義務を履行するため

c. その他、本サービスの運営上やむを得ない事由がある場合

3. 当社は、前項に基づきユーザーの情報を保持・利用する場合でも、本規約および法令の定めるところに従い適切に管理するものとします。`,
  },
  {
    title: '第19条 知的財産権等',
    content: `1. 本サービスに関する一切の知的財産権等は、すべて当社又は正当な権利者たる第三者に帰属する。

2. 本契約の成立は、本サービスの利用に必要な範囲を超えるユーザーに対する知的財産権の利用許諾を意味するものではない。ユーザーは、当社の事前の許可なく、本サービスの利用に必要な範囲を超える、本サービスの譲渡、貸与、公開、第三者提供、その他類似する一切の行為を行ってはならない。`,
  },
  {
    title: '第20条 機密情報および個人情報の取扱い',
    content: `1. ユーザーおよび当社は、利用契約に関して知りえた相手方の機密情報（本サービスに関するノウハウ、本サービスに関する情報、技術上または営業上の一切の機密情報を含みます。）および相手方の個人情報を、厳重かつ適正に管理するものとし、相手方の事前の書面による同意なく第三者に開示、提供および漏洩しないものとします。但し、法令又は裁判所の命令により開示が義務付けられた秘密情報を必要な範囲内で開示する場合は、この限りではありません。

2. ユーザーおよび当社は、相手方の指示があった場合または利用契約が終了した場合は、相手方の指示に従いすみやかに機密情報または個人情報を返却または廃棄し、以後使用しないものとします。ただし、第18条の規定に従い当社が利用する情報を除きます。`,
  },
  {
    title: '第21条 生成AIサービスを利用した機能について',
    content: `1. 当社が使用するAIサービスはOpenAI（およびその関連会社）が提供する「OpenAI API」、Google（およびその関連会社）が提供する「Gemini API」、その他当社が指定する第三者のAIサービス（以下、あわせて「AIサービス」といいます。）とします。当社は、当社の判断により、使用するAIサービスを変更または追加することができます。

2. ユーザーは、当社を介して本サービスの利用に必要な情報（個人情報は除く）をAIサービスに連携し、これをもとに得られた生成物（動画・画像・音声・台本・文章等。以下「生成物」という。）を自己のために利用することができる。

3. ユーザーは、前項で規定する情報のAIサービスへの連携に関してあらかじめ同意し、これに関して何ら異議を申し立てない。

4. ユーザーは、本サービスの利用の一環としてのみ生成物を使用するものとし、生成物の商業的利用、第三者への提供、不特定多数への公開については、関連する法令・第三者の権利・各AIサービスの利用規約の範囲内においてのみ行うものとします。

5. ユーザーは、AIサービスの提供元が定める遵守事項等の範囲内で生成物を利用する。

6. ユーザーは、AIサービスに連携される情報について、AIサービスの提供元が定める一定の期間内で情報が保存される場合があることについてあらかじめ承諾する。

7. 当社は、AIサービスの仕様変更や追加機能等による本サービスの中断、中止その他障害が生じないことを保証しない。

8. ユーザーは、自己の責任のもとで生成物を利用し、AIサービスを用いた機能等の利用および生成物の利用に起因または関連して生じうるあらゆる損害・紛争については自己の責任と費用負担においてこれに対処し、請求原因のいかんにかかわらず、当社に対して何らの請求を行わない。

9. 本サービスおよびその生成物は現状有姿で提供される。当社は、生成物の正確性、有用性、合法性、最新性、第三者の権利非侵害等を含むあらゆる事項について何ら保証するものではない。ユーザーは生成物を採用する際に、必ず自己の責任と判断により、内容の正確性・適法性・第三者の権利との抵触の有無等を確認し、必要に応じて修正・追補を行うものとする。

10. ユーザーが生成物を用いることにより、第三者の権利侵害や法令・ガイドライン違反等の問題が生じたとしても、当社は一切の責任を負わないものとし、ユーザーがこれらの問題に起因または関連して被った損害や第三者との間で発生した紛争などについては、ユーザー自身の責任と費用において解決するものとし、当社に対して何ら請求を行わないものとする。`,
  },
  {
    title: '第22条 反社会的勢力の排除',
    content: `1. ユーザーおよび当社は、自らが反社会的勢力（暴力団、暴力団員、暴力団員でなくなった時から5年を経過しない者、暴力団準構成員、暴力団関係企業、総会屋等、社会運動等標ぼうゴロまたは特殊知能暴力集団、その他これらに準ずる者をいいます。以下同じ。）に現在および将来にわたって該当しないこと、ならびに、反社会的勢力と以下の各号の一にでも該当する関係を現在および将来にわたって有しないことを誓約するものとします。

a. 反社会的勢力が経営に支配的な影響力を有すること

b. 反社会的勢力が経営に実質的に関与していること

c. 自己、自社もしくは第三者の不正の利益を図る目的または第三者に損害を加える目的をもって、不当に反社会的勢力を利用すること

d. 反社会的勢力に対して資金等を提供し、または便宜を供与するなどの関与をしていること

e. その他役員等または経営に実質的に関与している者が、反社会的勢力と社会的に非難されるべき関係を有すること

2. ユーザーおよび当社は、自己または第三者を利用して以下各号の行為を行ってはならないものとします。

a. 暴力的な要求行為

b. 法的な責任を超えた不当な要求行為

c. 取引に関して、脅迫的な言動をし、または暴力を用いる行為

d. 風説を流布し、偽計または威力を用いて相手方の信用を棄損し、または相手方の業務を妨害する行為

e. その他前各号に準ずる行為

3. ユーザーおよび当社は、前2項の該当性の判断のために調査を要すると判断した場合、相手方に対し調査に協力するよう求めることができるものとします。相手方は、これに必要な資料を提出するものとします。

4. ユーザーまたは当社は、相手方が本条の規定に違反した場合、何ら催告等の手続を要せず、ユーザーと当社の間にて締結された全ての契約を解除することができるものとします。この場合、契約の解除を行ったユーザーまたは当社は、相手方に損害が生じても何らこれを賠償ないし補償することを要しないものとします。また、解除を行ったユーザーまたは当社に損害が生じたときは、相手方はその損害を賠償するものとします。`,
  },
  {
    title: '第23条 損害賠償',
    content: `1. 当社は、ユーザーの本サービスの利用によりユーザーに生じた損害に対する賠償責任を負いません。

2. 前項にかかわらず、ユーザーに生じた損害（直接かつ通常の損害に限定され、弁護士費用を含みません。）が当社の故意または重過失によるものである場合、当社は、ユーザーから請求を受けた時から3ヶ月前までの期間に利用契約に基づき当社がユーザーから受領済の利用料総額を上限として、賠償に応じるものとします。

3. 前2項の規定は、債務不履行に基づく損害賠償請求のほか、契約不適合責任、不当利得、不法行為、解除およびその他請求原因の如何にかかわらず適用されるものとし、当社は、前2項に定めるもののほか、一切のユーザーの損害に対する賠償義務を負いません。`,
  },
  {
    title: '第24条 権利義務譲渡の禁止',
    content: `ユーザーは、本サービスに関する契約上の地位、およびこれに関して生じた当社に対する債権等いかなる権利義務も、当社の事前の書面による承諾なく第三者に移転、譲渡または担保に供することはできません。`,
  },
  {
    title: '第25条 連絡・通知',
    content: `本サービスに関する当社からユーザーに対する連絡又は通知は、当社の定める方法で行うものとします。`,
  },
  {
    title: '第26条 存続条項',
    content: `第9条（カスタマーハラスメントの禁止）、第18条（情報の利用等）、第19条（知的財産権等）、第20条（機密情報および個人情報の取扱い）、第21条（生成AIサービスを利用した機能について）、第23条（損害賠償）、第24条（権利義務譲渡の禁止）、第28条（準拠法）、第29条（合意管轄）、第30条（分離可能性）その他本規約の趣旨から当然に存続すべき条項は、本契約の終了後も有効に存続するものとします。`,
  },
  {
    title: '第27条 協議',
    content: `本規約に定めのない事項または本規約の条項について疑義が生じた場合、双方誠意をもって協議し、円満に解決するよう努めるものとします。`,
  },
  {
    title: '第28条 準拠法',
    content: `利用契約に関する準拠法は、日本国法とします。`,
  },
  {
    title: '第29条 合意管轄',
    content: `利用契約に関する一切の紛争は、当社の本店所在地を管轄する地方裁判所を専属的合意管轄裁判所とします。`,
  },
  {
    title: '第30条 分離可能性',
    content: `本規約のいずれかの条項またはその一部が無効と判断された場合であっても、無効と判断された部分以外については、継続して完全な効力を有するものとします。`,
  },
  {
    title: '第31条 事業者情報',
    content: `本サービスの運営主体は以下のとおりです。

【運営会社】
株式会社ラクダ

【提供サービス】
Genemo（Genemo Short / Genemo Page / Genemo Blog 等の Genemo シリーズ全般）

【代表者・所在地・お問い合わせ先】
代表者氏名・本店所在地・電話番号その他の事業者情報、および特定商取引法に基づく表示の詳細については、当社ウェブサイト上の「特定商取引法に基づく表示」ページにて公表します。`,
  },
]
